import React, { useEffect, useMemo, useState } from 'react';
import { Textarea } from 'rizzui';
import { toast } from 'sonner';
import ContractTemplateVariablesPanel from '../contract-templates/ContractTemplateVariablesPanel';
import { ProductTypeService } from '../../services/product-type/productTypeService';

const INVOICE_TYPES = ['client_invoice', 'tutor_invoice', 'internal_invoice'] as const;

interface VariableCategory {
    label: string;
    type: string;
    variables: Array<{ key: string; label: string }>;
}

export interface InvoiceConceptBillingValues {
    templateConceptClientInvoice: string;
    templateConceptTutorInvoice: string;
    templateConceptInternalInvoice: string;
}

export interface InvoiceConceptBillingErrors {
    templateConceptClientInvoice?: string;
    templateConceptTutorInvoice?: string;
    templateConceptInternalInvoice?: string;
}

interface InvoiceConceptBillingSectionProps {
    idPrefix: string;
    values: InvoiceConceptBillingValues;
    errors?: InvoiceConceptBillingErrors;
    onChange: (field: keyof InvoiceConceptBillingValues, value: string) => void;
}

const mergeVariableCategories = (categoriesList: VariableCategory[][]): VariableCategory[] => {
    const mergedByType = new Map<string, VariableCategory>();

    categoriesList.flat().forEach((category) => {
        const existing = mergedByType.get(category.type);
        if (!existing) {
            mergedByType.set(category.type, {
                ...category,
                variables: [...category.variables],
            });
            return;
        }

        const keys = new Set(existing.variables.map((v) => v.key));
        category.variables.forEach((variable) => {
            if (!keys.has(variable.key)) {
                existing.variables.push(variable);
                keys.add(variable.key);
            }
        });
    });

    return Array.from(mergedByType.values());
};

const InvoiceConceptBillingSection: React.FC<InvoiceConceptBillingSectionProps> = ({
    idPrefix,
    values,
    errors,
    onChange,
}) => {
    const [variables, setVariables] = useState<VariableCategory[]>([]);
    const productTypeService = useMemo(() => new ProductTypeService(), []);

    useEffect(() => {
        const loadVariables = async () => {
            try {
                const responses = await Promise.all(
                    INVOICE_TYPES.map((invoiceType) =>
                        productTypeService.listInvoiceConceptVariables(invoiceType)
                    )
                );

                const categoriesList = responses
                    .map((response) => response.getResponseData())
                    .filter((data) => data?.success)
                    .map((data) => data?.data?.variables || []) as VariableCategory[][];

                setVariables(mergeVariableCategories(categoriesList));
            } catch (error) {
                console.error('Error loading invoice concept variables:', error);
                toast.error('Error al cargar las variables de concepto de factura');
            }
        };

        loadVariables();
    }, [productTypeService]);

    return (
        <div className="col-span-full space-y-4">
            <ContractTemplateVariablesPanel variables={variables} />

            <Textarea
                id={`${idPrefix}-templateConceptClientInvoice`}
                label="Concepto factura cliente"
                rows={1}
                value={values.templateConceptClientInvoice}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    onChange('templateConceptClientInvoice', e.target.value)
                }
                error={errors?.templateConceptClientInvoice}
            />

            <Textarea
                id={`${idPrefix}-templateConceptTutorInvoice`}
                label="Concepto factura tutor"
                rows={1}
                value={values.templateConceptTutorInvoice}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    onChange('templateConceptTutorInvoice', e.target.value)
                }
                error={errors?.templateConceptTutorInvoice}
            />

            <Textarea
                id={`${idPrefix}-templateConceptInternalInvoice`}
                label="Concepto factura interna"
                rows={1}
                value={values.templateConceptInternalInvoice}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    onChange('templateConceptInternalInvoice', e.target.value)
                }
                error={errors?.templateConceptInternalInvoice}
            />

            <p className="text-xs text-gray-500">
                Si un campo está vacío, se usará el código del contrato como concepto al generar la factura.
            </p>
        </div>
    );
};

export default InvoiceConceptBillingSection;
